import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { profileApi } from '@/api/auth';
import { fetchHomeSearchHelpItems } from '@/api/home';
import { fetchSearchQuota, recordHomeSearch, type SearchQuota } from '@/api/search';
import {
  DEFAULT_HOME_SEARCH_HELP_ITEMS,
  type HomeSearchHelpItem,
} from '@/constants/home-search-help';
import { getToken } from '@/lib/auth-storage';
import { openSaleSmartlyChat } from '@/lib/salesmartly';
import { parseSearchApiResponse } from '@/utils/search-result';

const stats = [
  { value: '50万+', label: '累计用户' },
  { value: '99%', label: '查询成功率' },
  { value: '24h', label: '全天候服务' },
  { value: '3分钟', label: '平均出结果' },
];

/** 首页查询 API 暂时不可用时设为 false，恢复时改回 true */
const HOME_SEARCH_UI_ENABLED = false;

const SEARCH_FAILURE_REASON = '没有找到相关信息，请尝试重新查询或等待数据更新';
const POINTS_RECHARGE_MESSAGE = '积分充值咨询';

type InviteCopyStatus = '' | 'copying' | 'copied' | 'failed' | 'login';

function formatQuotaLine(quota: SearchQuota | null): string {
  if (!quota) {
    return '';
  }
  const free = quota.freeSearchRemaining ?? 0;
  if (free > 0) {
    return `赠送搜索剩余 ${free} 次（不占会员合计额度）`;
  }
  if (quota.memberActive) {
    const used = quota.dailyCombinedUsed ?? 0;
    const limit = quota.dailyCombinedLimit ?? 0;
    const rem = quota.dailyCombinedRemaining ?? Math.max(0, limit - used);
    return `今日已用 ${used} / ${limit} 次（查单+搜索合计），剩余 ${rem} 次`;
  }
  const pts = quota.pointsBalance ?? 0;
  const cost = quota.searchPointsCost ?? 2;
  return `当前积分 ${pts}，每次查询消耗 ${cost}积分`;
}

function normalizeHelpItems(items?: HomeSearchHelpItem[]): HomeSearchHelpItem[] {
  const source = items && items.length > 0 ? items : DEFAULT_HOME_SEARCH_HELP_ITEMS;
  const normalized = source
    .filter((item) => item && item.enabled !== false)
    .map((item) => ({
      ...item,
      examples: Array.isArray(item.examples) && item.examples.length > 0
        ? item.examples
        : item.example
          ? [item.example]
          : [],
      helpLines: Array.isArray(item.helpLines) ? item.helpLines : [],
      tips: Array.isArray(item.tips) ? item.tips : [],
      sortOrder: item.sortOrder ?? 0,
    }))
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return normalized.length > 0 ? normalized : DEFAULT_HOME_SEARCH_HELP_ITEMS;
}

function getExamples(item: HomeSearchHelpItem | null): string[] {
  if (!item) {
    return [];
  }
  if (Array.isArray(item.examples) && item.examples.length > 0) {
    return item.examples;
  }
  return item.example ? [item.example] : [];
}

export default function HeroSection() {
  const [searchValue, setSearchValue] = useState('');
  const [quota, setQuota] = useState<SearchQuota | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [searchSubmitting, setSearchSubmitting] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [inviteCopyStatus, setInviteCopyStatus] = useState<InviteCopyStatus>('');
  const [helpItems, setHelpItems] = useState<HomeSearchHelpItem[]>(
    normalizeHelpItems(DEFAULT_HOME_SEARCH_HELP_ITEMS),
  );
  const [activeHelpKey, setActiveHelpKey] = useState<string>('');
  const [exampleIndexes, setExampleIndexes] = useState<Record<string, number>>({});
  const navigate = useNavigate();

  const activeHelp = helpItems.find((item) => item.key === activeHelpKey) ?? null;
  const activeExamples = getExamples(activeHelp);
  const rawActiveExampleIndex = activeHelp ? exampleIndexes[activeHelp.key] ?? 0 : 0;
  const activeExampleIndex = activeExamples.length > 0
    ? Math.min(rawActiveExampleIndex, activeExamples.length - 1)
    : 0;
  const activeExample = activeExamples[activeExampleIndex] ?? activeHelp?.example ?? '';
  const isInsufficientPoints = searchError.includes('积分不足');

  const loadQuota = useCallback(() => {
    if (!getToken()) {
      setQuota(null);
      return;
    }
    setQuotaLoading(true);
    fetchSearchQuota()
      .then((res) => {
        if (res.code === 0 && res.data) {
          setQuota(res.data);
        } else {
          setQuota(null);
        }
      })
      .catch(() => setQuota(null))
      .finally(() => setQuotaLoading(false));
  }, []);

  useEffect(() => {
    loadQuota();
    const onAuth = () => loadQuota();
    window.addEventListener('mh-auth-changed', onAuth);
    window.addEventListener('storage', onAuth);
    return () => {
      window.removeEventListener('mh-auth-changed', onAuth);
      window.removeEventListener('storage', onAuth);
    };
  }, [loadQuota]);

  useEffect(() => {
    let mounted = true;
    fetchHomeSearchHelpItems()
      .then((res) => {
        if (!mounted || res.code !== 0) {
          return;
        }
        setHelpItems(normalizeHelpItems(res.data));
      })
      .catch(() => {
        if (mounted) {
          setHelpItems(normalizeHelpItems(DEFAULT_HOME_SEARCH_HELP_ITEMS));
        }
      });
    return () => {
      mounted = false;
    };
  }, []);

  const handleHelpClick = (item: HomeSearchHelpItem) => {
    const examples = getExamples(item);
    const nextIndex = activeHelpKey === item.key
      ? ((exampleIndexes[item.key] ?? -1) + 1) % Math.max(examples.length, 1)
      : 0;
    setActiveHelpKey(item.key);
    setExampleIndexes((prev) => ({ ...prev, [item.key]: nextIndex }));
    setSearchValue(examples[nextIndex] ?? item.example ?? '');
    setSearchError('');
    setInviteCopyStatus('');
  };

  const handleRechargeConsult = () => {
    openSaleSmartlyChat({ message: POINTS_RECHARGE_MESSAGE });
  };

  const handleCopyInviteLink = async () => {
    if (!getToken()) {
      setInviteCopyStatus('login');
      return;
    }

    setInviteCopyStatus('copying');
    try {
      const res = await profileApi();
      const code = res.code === 0 ? res.data?.referralCode?.trim() : '';
      if (!code) {
        setInviteCopyStatus('failed');
        return;
      }
      const inviteLink = `${window.location.origin}/register?invite=${encodeURIComponent(code)}`;
      await navigator.clipboard.writeText(inviteLink);
      setInviteCopyStatus('copied');
      window.setTimeout(() => setInviteCopyStatus(''), 2000);
    } catch {
      setInviteCopyStatus('failed');
    }
  };

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    const q = searchValue.trim();
    if (!q) {
      return;
    }
    setSearchError('');
    setInviteCopyStatus('');
    const target = `/services?q=${encodeURIComponent(q)}`;

    if (!getToken()) {
      navigate(target);
      return;
    }

    setSearchSubmitting(true);
    try {
      const res = await recordHomeSearch(q);
      if (res.code !== 0) {
        setSearchError(res.msg || '搜索失败');
        return;
      }
      if (res.data) {
        setQuota(res.data);
      }
      const searchOrderId = res.data?.searchOrderId;
      if (res.data?.status === '3') {
        if (!searchOrderId) {
          setSearchError('搜索记录异常，请重试');
          return;
        }
        navigate(`/search/results?sid=${searchOrderId}&q=${encodeURIComponent(q)}`, {
          state: {
            failureReason: res.data.failureReason || SEARCH_FAILURE_REASON,
            keyword: q,
            pointsBalance: res.data.pointsBalance,
            responseJson: res.data.responseJson,
            searchOrderId,
            status: res.data.status,
          },
        });
        return;
      }
      const parsed = parseSearchApiResponse(res.data?.responseJson);
      if (!parsed.ok) {
        setSearchError(parsed.message || '搜索失败');
        return;
      }
      if (parsed.rows.length === 0) {
        setSearchError(parsed.message || '未查询到匹配数据');
        return;
      }
      if (!searchOrderId) {
        setSearchError('搜索记录异常，请重试');
        return;
      }
      navigate(`/search/results?sid=${searchOrderId}&q=${encodeURIComponent(q)}`, {
        state: { responseJson: res.data?.responseJson, keyword: q },
      });
    } catch {
      setSearchError('网络异常，请稍后重试');
    } finally {
      setSearchSubmitting(false);
    }
  };

  return (
    <section className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-600 overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-blue-300/10 rounded-full blur-3xl" />
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_rgba(255,255,255,0.05)_0%,_transparent_60%)]" />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.3) 1px, transparent 1px)', backgroundSize: '40px 40px' }}
        />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-0">
        <div className="flex justify-center">
          <div className="w-full max-w-3xl text-white text-center">
            <div className="inline-flex items-center px-4 py-1.5 bg-white/10 backdrop-blur-md rounded-full text-sm font-medium mb-4 border border-white/20">
              <span className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse" />
              中文用户第一大选择
            </div>

            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 leading-tight tracking-tight">
              查191 全网信息查询平台
            </h1>

            <p className="text-blue-100 text-sm mb-1 font-medium">
              基于AI技术的智能查询系统
            </p>
            <p className="text-blue-200/80 text-xs mb-5 max-w-lg leading-relaxed mx-auto">
              怀疑、猜测、忍让只会让你误入歧途，只有找出真相才是解决问题的根本
            </p>

            {/* Search box */}
            <form onSubmit={handleSearch} className="w-full max-w-lg mx-auto mb-4">
              <div className="flex items-center bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl overflow-hidden focus-within:border-white/60 focus-within:bg-white/20 transition-all duration-200">
                <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 pl-3">
                  <i className="ri-search-line text-white/70 text-lg" />
                </div>
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="免费查档服务, 输入例如：手机号码或身份证号码"
                  className="flex-1 bg-transparent text-white placeholder-white/50 text-sm px-3 py-3.5 outline-none"
                />
                <button
                  type="submit"
                  disabled={searchSubmitting}
                  className="m-1.5 px-5 py-2 bg-white text-blue-700 font-semibold text-sm rounded-xl hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
                >
                  {searchSubmitting ? '提交中…' : '搜索'}
                </button>
              </div>
              {getToken() && (
                <p className="text-blue-200/90 text-xs mt-2 min-h-[1.25rem]">
                  {quotaLoading ? '加载额度中…' : formatQuotaLine(quota)}
                </p>
              )}
              {!getToken() && (
                <p className="text-blue-200/70 text-xs mt-2">
                  登录后可使用赠送搜索、会员额度或积分进行查询
                </p>
              )}
              <Link
                to="/affiliate"
                className="inline-flex items-center justify-center gap-1 text-xs text-white font-medium mt-2 hover:text-blue-100 transition-colors cursor-pointer"
              >
                邀请好友，获取积分及现金奖励
                <i className="ri-arrow-right-line" />
              </Link>
              {searchError && (
                isInsufficientPoints ? (
                  <div className="mt-2 flex items-center justify-center gap-3 text-xs">
                    <button
                      type="button"
                      onClick={handleRechargeConsult}
                      className="text-amber-200 font-semibold underline underline-offset-2 hover:text-amber-100 transition-colors cursor-pointer"
                    >
                      积分不足
                    </button>
                    <span className="h-3 w-px bg-white/25" />
                    <button
                      type="button"
                      onClick={handleCopyInviteLink}
                      disabled={inviteCopyStatus === 'copying'}
                      className="inline-flex items-center gap-1 text-white font-semibold hover:text-blue-100 transition-colors cursor-pointer disabled:opacity-60"
                    >
                      <i className="ri-share-forward-line" />
                      {inviteCopyStatus === 'copying'
                        ? '复制中…'
                        : inviteCopyStatus === 'copied'
                          ? '已复制专享邀请链接长按粘贴分享'
                          : '分享得积分'}
                    </button>
                    {(inviteCopyStatus === 'failed' || inviteCopyStatus === 'login') && (
                      <span className="text-amber-200">
                        {inviteCopyStatus === 'login' ? '请先登录' : '复制失败'}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="text-amber-200 text-xs mt-2">{searchError}</p>
                )
              )}
            </form>

            {HOME_SEARCH_UI_ENABLED && (
              <>
                {/* Search help buttons */}
                <div className="w-full max-w-2xl mx-auto mb-5">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {helpItems.map((item) => {
                      const active = activeHelpKey === item.key;
                      return (
                        <button
                          key={item.key}
                          type="button"
                          onClick={() => handleHelpClick(item)}
                          className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded-2xl border transition-all duration-200 cursor-pointer ${
                            active
                              ? 'bg-white text-blue-700 border-white shadow-lg shadow-blue-950/10'
                              : 'bg-white/15 text-white border-white/20 hover:bg-white/25 hover:border-white/40'
                          }`}
                        >
                          <i className={`${item.icon || 'ri-search-line'} text-lg`} />
                          <span className="text-xs font-semibold whitespace-nowrap">{item.label}</span>
                        </button>
                      );
                    })}
                  </div>

                  {activeHelp && (
                    <div className="mt-3 text-left bg-white/15 border border-white/25 rounded-2xl p-4 backdrop-blur-md">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-white text-sm font-semibold">{activeHelp.helpTitle || activeHelp.label}</p>
                          <p className="text-blue-100/80 text-xs mt-1">
                            已自动填入示例，可直接修改后搜索
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setSearchValue(activeExample)}
                          className="text-[11px] px-2.5 py-1 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          重填示例
                        </button>
                      </div>

                      {activeExample && (
                        <div className="mt-3 rounded-xl bg-blue-950/20 border border-white/10 px-3 py-2">
                          <p className="text-blue-100/70 text-[11px] mb-1">
                            当前填入示例 {activeExamples.length > 1 ? `${activeExampleIndex + 1}/${activeExamples.length}` : ''}
                          </p>
                          <p className="text-white text-xs leading-relaxed">{activeExample}</p>
                        </div>
                      )}

                      {activeExamples.length > 1 && (
                        <div className="mt-3 rounded-xl bg-white/10 border border-white/10 px-3 py-2">
                          <p className="text-blue-100/70 text-[11px] mb-1">全部示例（再次点击按钮会轮换填入）</p>
                          <div className="space-y-1">
                            {activeExamples.map((example, index) => (
                              <p
                                key={`${activeHelp.key}-example-${index}`}
                                className={`text-xs leading-relaxed ${
                                  index === activeExampleIndex ? 'text-white font-semibold' : 'text-blue-100/80'
                                }`}
                              >
                                {index + 1}. {example}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {activeHelp.helpLines.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                          {activeHelp.helpLines.map((line, index) => (
                            <p key={`${activeHelp.key}-help-${index}`} className="text-blue-50/90 text-xs leading-relaxed">
                              {line}
                            </p>
                          ))}
                        </div>
                      )}

                      {activeHelp.tips.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <p className="text-white/80 text-[11px] font-semibold mb-1">使用建议</p>
                          <ul className="space-y-1">
                            {activeHelp.tips.map((tip, index) => (
                              <li key={`${activeHelp.key}-tip-${index}`} className="flex gap-1.5 text-blue-100/80 text-xs leading-relaxed">
                                <span className="mt-1 w-1 h-1 rounded-full bg-blue-100/70 flex-shrink-0" />
                                <span>{tip}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}

          </div>
        </div>

        
      </div>

    </section>
  );
}
